import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DesktopSellerPageContainer } from '@/components/desktop/DesktopSellerPageContainer';
import { sellerService } from '@/services/seller';
import { useSellerStore } from '@/store/seller';
import { StoreDetailPage } from '@/pages/store/StoreDetailPage';

export function DesktopStoreMonitoringPage() {
  const navigate = useNavigate();
  const { setLoading, setError } = useSellerStore();
  
  const [restaurantId, setRestaurantId] = useState<string>('');

  useEffect(() => {
    loadRestaurantId();
  }, []);

  const loadRestaurantId = async () => {
    try {
      setLoading(true);
      
      const restaurantResult = await sellerService.getSellerRestaurantId();
      if (!restaurantResult.success || !restaurantResult.restaurantId) {
        setError(restaurantResult.error || '레스토랑 정보를 찾을 수 없습니다');
        return;
      }

      setRestaurantId(restaurantResult.restaurantId);
      
    } catch (error) {
      console.error('Restaurant ID load error:', error);
      setError('레스토랑 정보를 불러오는 중 오류가 발생했습니다');
    } finally {
      setLoading(false);
    }
  };

  if (!restaurantId) {
    return (
      <DesktopSellerPageContainer>
        <div className="flex items-center justify-center min-h-[600px]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-green-500 mx-auto mb-6"></div>
            <p className="text-[18px] text-gray-600">레스토랑 정보를 불러오는 중...</p>
          </div>
        </div>
      </DesktopSellerPageContainer>
    );
  }

  return (
    <DesktopSellerPageContainer>
      <div className="max-w-6xl mx-auto">
        {/* Page Header */}
        <div className="mb-8 flex items-center">
          <button
            onClick={() => navigate(-1)}
            className="p-2 text-gray-600 hover:text-gray-800 mr-4"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <h1 className="text-[32px] font-bold text-gray-800">내 가게 모니터링</h1>
        </div>

        {/* Monitoring Information */}
        <div className="mb-8 bg-blue-50 border border-blue-200 rounded-xl p-6">
          <h2 className="text-[24px] font-semibold text-blue-800 mb-3">소비자 관점에서 내 가게 확인</h2>
          <p className="text-[16px] text-blue-700 mb-4">
            고객이 실제로 보는 내 가게 정보를 확인할 수 있습니다. 
            상품 정보, 가게 소개, 영업시간 등이 올바르게 표시되는지 점검해보세요.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white rounded-lg p-4 border border-blue-200">
              <h3 className="text-[16px] font-semibold text-blue-800 mb-2">확인 포인트</h3>
              <ul className="text-[14px] text-blue-700 space-y-1">
                <li>• 가게 정보 정확성</li>
                <li>• 상품 이미지 품질</li>
                <li>• 가격 및 할인 정보</li>
              </ul>
            </div>
            <div className="bg-white rounded-lg p-4 border border-blue-200">
              <h3 className="text-[16px] font-semibold text-blue-800 mb-2">개선 사항</h3>
              <ul className="text-[14px] text-blue-700 space-y-1">
                <li>• 매력적인 가게 소개</li>
                <li>• 고화질 상품 사진</li>
                <li>• 정확한 영업시간</li>
              </ul>
            </div>
            <div className="bg-white rounded-lg p-4 border border-blue-200">
              <h3 className="text-[16px] font-semibold text-blue-800 mb-2">고객 만족도</h3>
              <ul className="text-[14px] text-blue-700 space-y-1">
                <li>• 리뷰 및 평점 관리</li>
                <li>• 빠른 주문 처리</li>
                <li>• 친절한 고객 응대</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Store Detail Content */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden min-h-[800px]">
          {/* 모니터링 뷰 */}
          <div className="p-6">
            <div className="mb-4 text-center">
              <span className="inline-block bg-gray-100 text-gray-700 px-4 py-2 rounded-lg text-[16px] font-medium">
                📱 고객이 보는 화면
              </span>
            </div>
            <div className="bg-gray-50 border border-gray-200 rounded-xl overflow-hidden">
              <StoreDetailPage storeId={restaurantId} readonly={true} />
            </div>
          </div>
        </div>

        {/* Additional Tips */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-green-50 border border-green-200 rounded-lg p-6">
            <h3 className="text-[18px] font-semibold text-green-800 mb-3">💡 개선 팁</h3>
            <ul className="space-y-2 text-[16px] text-green-700">
              <li>• 정기적으로 가게 정보를 업데이트하세요</li>
              <li>• 고화질 상품 이미지를 사용하세요</li>
              <li>• 정확한 영업시간을 설정하세요</li>
              <li>• 매력적인 가게 소개글을 작성하세요</li>
            </ul>
          </div>

          <div className="bg-orange-50 border border-orange-200 rounded-lg p-6">
            <h3 className="text-[18px] font-semibold text-orange-800 mb-3">⚠️ 주의사항</h3>
            <ul className="space-y-2 text-[16px] text-orange-700">
              <li>• 실제 상품과 다른 이미지는 사용하지 마세요</li>
              <li>• 과장된 할인율이나 가격 표시는 금지입니다</li>
              <li>• 영업시간과 실제 운영시간을 일치시키세요</li>
              <li>• 부정확한 정보는 고객 불만으로 이어집니다</li>
            </ul>
          </div>
        </div>
      </div>
    </DesktopSellerPageContainer>
  );
}